import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Dog as DogIcon, Mail, Phone, MapPin, Heart, Stethoscope, Pill, AlertTriangle, ShieldCheck, ShieldAlert, IdCard, UserCog, ChevronRight, FileText, Plus, UserPlus, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminClients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [q, setQ] = useState('');
  const [openDog, setOpenDog] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  // Add-Client dialog state
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [newClient, setNewClient] = useState({ full_name: '', email: '', phone: '' });
  const [creating, setCreating] = useState(false);
  const [tempPwReveal, setTempPwReveal] = useState(null); // { email, password }
  const [copied, setCopied] = useState(false);
  // Add-Dog dialog state
  const [addDogOpen, setAddDogOpen] = useState(false);
  const [addDogClientId, setAddDogClientId] = useState(null);
  const [newDog, setNewDog] = useState({ name: '', breed: '', age_years: '', sex: '', weight_lbs: '' });
  const [creatingDog, setCreatingDog] = useState(false);

  const load = async () => {
    const { data } = await api.get('/admin/clients', { params: { include_archived: includeArchived } });
    setRows(data);
  };

  useEffect(() => {
    load();
    api.get('/trainers/list').then(r => setTrainers(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  const filtered = rows.filter(r => !q || [r.full_name, r.email, r.phone].some(x => (x||'').toLowerCase().includes(q.toLowerCase())));

  const setAssigned = async (dogId, trainerId) => {
    try {
      await api.post(`/dogs/${dogId}/assign-trainer`, { trainer_id: trainerId || null });
      toast.success(trainerId ? 'Trainer assigned' : 'Trainer unassigned');
      // refresh openDog if needed
      if (openDog?.id === dogId) {
        const { data } = await api.get(`/dogs/${dogId}`);
        setOpenDog(data);
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const canAssign = user && ['ADMIN','FRONT_DESK','TRAINER'].includes(user.role);
  const canCreate = user && ['ADMIN','FRONT_DESK'].includes(user.role);
  const canArchive = user && user.role === 'ADMIN';

  const submitNewClient = async () => {
    if (!newClient.full_name.trim() || !newClient.email.trim()) {
      toast.error('Name and email are required'); return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/admin/clients', newClient);
      setTempPwReveal({ email: data.user.email, password: data.temp_password });
      setNewClient({ full_name: '', email: '', phone: '' });
      await load();
      toast.success('Client created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create client');
    } finally { setCreating(false); }
  };

  const copyPassword = async () => {
    if (!tempPwReveal) return;
    try {
      await navigator.clipboard.writeText(`${tempPwReveal.email} / ${tempPwReveal.password}`);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch { toast.error('Clipboard blocked by browser'); }
  };

  const openAddDog = (e, clientId) => {
    e.stopPropagation();
    setAddDogClientId(clientId);
    setNewDog({ name: '', breed: '', age_years: '', sex: '', weight_lbs: '' });
    setAddDogOpen(true);
  };

  const submitNewDog = async () => {
    if (!newDog.name.trim()) { toast.error("Dog's name is required"); return; }
    setCreatingDog(true);
    try {
      const payload = {
        owner_id: addDogClientId,
        name: newDog.name,
        breed: newDog.breed || null,
        age_years: newDog.age_years ? Number(newDog.age_years) : null,
        weight_lbs: newDog.weight_lbs ? Number(newDog.weight_lbs) : null,
        sex: newDog.sex || null,
      };
      await api.post('/admin/dogs', payload);
      setAddDogOpen(false);
      await load();
      toast.success('Dog added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add dog');
    } finally { setCreatingDog(false); }
  };

  return (
    <div className='p-6 lg:p-8'>
      <div className='flex items-start justify-between mb-4 gap-3 flex-wrap'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Directory</div>
          <h1 className='font-display text-3xl font-bold text-slate-900 dark:text-slate-50 mt-1'>Clients & Dogs</h1>
          <p className='text-slate-500 dark:text-slate-400 text-sm mt-1'>{rows.length} clients · {rows.reduce((s,r)=>s+r.dogs_count,0)} dogs</p>
        </div>
        <div className='flex items-center gap-2 flex-wrap justify-end'>
          <Input placeholder='Search name or email…' value={q} onChange={(e)=>setQ(e.target.value)} className='w-64 bg-white border-slate-200 text-slate-900 dark:bg-slate-900 dark:text-slate-100 dark:border-slate-700' data-testid='clients-search-input' />
          <label className='flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer px-2'>
            <Switch
              checked={includeArchived}
              onCheckedChange={setIncludeArchived}
              data-testid='clients-archived-toggle'
            />
            Show archived
          </label>
          {canCreate && (
            <Button
              onClick={() => setAddClientOpen(true)}
              className='bg-[#C8102E] hover:bg-[#A50D26] text-white shadow-md font-semibold'
              data-testid='add-client-btn'
            >
              <UserPlus className='w-4 h-4 mr-2' /> Add Client
            </Button>
          )}
        </div>
      </div>
      <div className='grid lg:grid-cols-2 gap-4' data-testid='clients-grid'>
        {filtered.map(c => (
          <Card key={c.id} className='p-5 bg-white border-slate-200 hover:border-[#1B3A87] cursor-pointer transition-colors' onClick={()=>navigate(`/admin/clients/${c.id}`)} data-testid={`client-card-${c.id}`}>
            <div className='flex items-start justify-between mb-3'>
              <div className='min-w-0 flex-1'>
                <div className='font-display font-bold text-lg text-slate-900 truncate'>{c.full_name}</div>
                <div className='text-xs text-slate-500 flex items-center gap-3 mt-1 flex-wrap'>
                  <span className='flex items-center gap-1'><Mail className='w-3 h-3' /> {c.email}</span>
                  {c.phone && <span className='flex items-center gap-1'><Phone className='w-3 h-3' /> {c.phone}</span>}
                </div>
                {(c.address_line1 || c.city) && (
                  <div className='text-xs text-slate-500 mt-1 flex items-center gap-1'><MapPin className='w-3 h-3' /> {[c.address_line1, c.city, c.state, c.postal_code].filter(Boolean).join(', ')}</div>
                )}
            </div>
            {/* Archived badge */}
            {c.archived_at && (
              <Badge className='bg-slate-400 text-white ml-1 shrink-0'>Archived</Badge>
            )}
            <Badge className='bg-[#1B3A87] text-white ml-2 shrink-0'>{c.bookings_count} bookings</Badge>
            {c.notes_count > 0 && <Badge className='bg-[#B08D57] text-white ml-1 shrink-0'><FileText className='w-3 h-3 mr-1' />{c.notes_count}</Badge>}
            <ChevronRight className='w-4 h-4 text-slate-500 ml-1 shrink-0' />
          </div>
          <div className='border-t border-slate-200 dark:border-slate-700 pt-3'>
            <div className='flex items-center justify-between mb-2'>
              <div className='text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1'><DogIcon className='w-3 h-3' /> {c.dogs_count} Dogs</div>
              {canCreate && (
                <Button
                  size='sm'
                  variant='outline'
                  className='h-6 px-2 text-[11px] border-slate-300 dark:border-slate-600 dark:text-slate-200'
                  onClick={(e) => openAddDog(e, c.id)}
                  data-testid={`add-dog-btn-${c.id}`}
                >
                  <Plus className='w-3 h-3 mr-1' /> Add Dog
                </Button>
              )}
            </div>
              {c.dogs.length === 0 ? <div className='text-xs text-slate-500'>No dogs registered</div> : (
                <div className='grid grid-cols-2 gap-2'>
                  {c.dogs.map(d => (
                    <button key={d.id} className='p-2 rounded bg-slate-50 border border-slate-200 text-xs text-left hover:border-[#1B3A87]' onClick={(e)=>{e.stopPropagation(); setOpenDog(d);}} data-testid={`open-dog-${d.id}`}>
                      <div className='flex items-center justify-between'>
                        <div className='font-semibold text-slate-900 truncate'>{d.name}</div>
                        {d.vax_compliant ? <ShieldCheck className='w-3 h-3 text-[#167A5A] shrink-0' /> : <ShieldAlert className='w-3 h-3 text-[#B45309] shrink-0' />}
                      </div>
                      <div className='text-slate-500 text-[10px] truncate'>{d.breed || '—'}</div>
                      {d.assigned_trainer_name && <div className='text-[#1B3A87] text-[10px] font-semibold mt-1 truncate'>→ {d.assigned_trainer_name}</div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <Card className='p-10 text-center text-slate-500 bg-white border-slate-200'>No clients yet.</Card>}
      </div>

      {/* Dog detail */}
      <Dialog open={!!openDog} onOpenChange={(v)=>!v && setOpenDog(null)}>
        <DialogContent className='max-w-2xl max-h-[92vh] overflow-y-auto'>
          <DialogHeader><DialogTitle className='flex items-center gap-2'><DogIcon className='w-5 h-5' /> {openDog?.name} · <span className='font-mono text-sm font-normal text-slate-500'>{openDog?.breed}</span></DialogTitle></DialogHeader>
          {openDog && (
            <div className='space-y-4 text-sm'>
              {openDog.photo_base64 && <img src={openDog.photo_base64} alt={openDog.name} className='w-full h-56 object-cover rounded-lg' />}

              {canAssign && (
                <div className='p-3 rounded-lg border border-[#1B3A87] bg-[#1B3A87]/5'>
                  <div className='flex items-center gap-2 text-[#1B3A87] mb-2'><UserCog className='w-4 h-4' /> <strong>Assigned Trainer</strong></div>
                  <div className='flex items-center gap-2'>
                    <select className='flex-1 h-10 rounded-md border border-slate-300 px-3 text-sm bg-white text-black' value={openDog.assigned_trainer_id || ''} onChange={(e)=>setAssigned(openDog.id, e.target.value || null)} data-testid='assign-trainer-select'>
                      <option value=''>— Unassigned —</option>
                      {trainers.map(t => {
                        const allowed = user?.role !== 'TRAINER' || t.id === user.id;
                        return <option key={t.id} value={t.id} disabled={!allowed}>{t.full_name}{!allowed ? ' (other trainer)' : ''}</option>;
                      })}
                    </select>
                  </div>
                  {user?.role === 'TRAINER' && <div className='text-[11px] text-slate-500 mt-1'>Trainers can only assign themselves.</div>}
                </div>
              )}

              <div className='grid grid-cols-2 gap-3'>
                <div className='p-3 rounded bg-slate-50 border border-slate-200'><div className='text-[10px] uppercase tracking-wider font-bold text-slate-500'>Owner</div><div className='font-semibold mt-1'>{openDog.owner_name}</div><div className='text-xs text-slate-600'>{openDog.owner_email}</div>{openDog.owner_phone && <div className='text-xs text-slate-600 font-mono'>{openDog.owner_phone}</div>}</div>
                <div className='p-3 rounded bg-slate-50 border border-slate-200'><div className='text-[10px] uppercase tracking-wider font-bold text-slate-500'>Identifiers</div>{openDog.microchip_number && <div className='text-xs mt-1'><IdCard className='w-3 h-3 inline mr-1' /> Chip: <span className='font-mono'>{openDog.microchip_number}</span></div>}{openDog.registration_number && <div className='text-xs mt-0.5'>Reg #: <span className='font-mono'>{openDog.registration_number}</span></div>}{openDog.spay_neuter && <div className='text-xs mt-0.5 capitalize'>{openDog.spay_neuter}</div>}</div>
              </div>

              {(openDog.allergies || openDog.medications || openDog.dietary_notes) && (
                <div className='p-3 rounded bg-[#C8102E]/5 border border-[#C8102E]/20 space-y-1.5'>
                  <div className='flex items-center gap-1 text-[#C8102E] text-xs font-bold uppercase tracking-wider'><Pill className='w-3 h-3' /> Medical & Diet</div>
                  {openDog.allergies && <div><strong>Allergies:</strong> {openDog.allergies}</div>}
                  {openDog.medications && <div><strong>Medications:</strong> {openDog.medications}</div>}
                  {openDog.dietary_notes && <div><strong>Diet:</strong> {openDog.dietary_notes}</div>}
                </div>
              )}

              {(openDog.vet_name || openDog.vet_phone) && (
                <div className='p-3 rounded bg-[#167A5A]/5 border border-[#167A5A]/20'>
                  <div className='flex items-center gap-1 text-[#167A5A] text-xs font-bold uppercase tracking-wider mb-1'><Stethoscope className='w-3 h-3' /> Veterinarian</div>
                  <div className='text-sm'>{openDog.vet_name} {openDog.vet_phone && <span className='font-mono ml-2'>{openDog.vet_phone}</span>}</div>
                  {openDog.vet_address && <div className='text-xs text-slate-600'>{openDog.vet_address}</div>}
                </div>
              )}

              {(openDog.emergency_contact_name || openDog.emergency_contact_phone) && (
                <div className='p-3 rounded bg-[#B45309]/5 border border-[#B45309]/20'>
                  <div className='flex items-center gap-1 text-[#B45309] text-xs font-bold uppercase tracking-wider mb-1'><Heart className='w-3 h-3' /> Emergency Contact</div>
                  <div className='text-sm'>{openDog.emergency_contact_name} {openDog.emergency_contact_relationship && <span className='text-slate-500'>({openDog.emergency_contact_relationship})</span>}</div>
                  {openDog.emergency_contact_phone && <div className='font-mono text-sm'>{openDog.emergency_contact_phone}</div>}
                </div>
              )}

              {(openDog.behavior_notes || openDog.training_needs) && (
                <div className='grid sm:grid-cols-2 gap-3'>
                  {openDog.behavior_notes && <div className='p-3 rounded bg-slate-50 border border-slate-200'><div className='text-[10px] uppercase tracking-wider font-bold text-slate-500'>Behavior</div><div className='mt-1 text-sm whitespace-pre-wrap'>{openDog.behavior_notes}</div></div>}
                  {openDog.training_needs && <div className='p-3 rounded bg-slate-50 border border-slate-200'><div className='text-[10px] uppercase tracking-wider font-bold text-slate-500'>Training Goals</div><div className='mt-1 text-sm whitespace-pre-wrap'>{openDog.training_needs}</div></div>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Client dialog */}
      <Dialog open={addClientOpen} onOpenChange={(v)=>{ setAddClientOpen(v); if(!v) setTempPwReveal(null); }}>
        <DialogContent className='max-w-md' data-testid='add-client-dialog'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <UserPlus className='w-5 h-5 text-[#C8102E]' /> Add new client
            </DialogTitle>
          </DialogHeader>
          {tempPwReveal ? (
            <div className='space-y-3' data-testid='temp-password-reveal'>
              <div className='p-4 rounded-lg bg-emerald-50 border border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700/40'>
                <div className='text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300'>Client created</div>
                <div className='text-sm text-slate-700 dark:text-slate-200 mt-2'>Share these credentials with the client &mdash; this password will <strong>only be shown once</strong>.</div>
                <div className='mt-3 p-3 rounded bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-700/50 font-mono text-sm'>
                  <div className='text-slate-500 dark:text-slate-400 text-[10px] uppercase tracking-wider'>Email</div>
                  <div className='font-semibold'>{tempPwReveal.email}</div>
                  <div className='text-slate-500 dark:text-slate-400 text-[10px] uppercase tracking-wider mt-2'>Temporary Password</div>
                  <div className='font-bold text-base'>{tempPwReveal.password}</div>
                </div>
                <Button
                  onClick={copyPassword}
                  className='mt-3 w-full bg-[#1B3A87] hover:bg-[#16306F] text-white'
                  data-testid='copy-temp-password-btn'
                >
                  {copied ? <><Check className='w-4 h-4 mr-2'/> Copied!</> : <><Copy className='w-4 h-4 mr-2'/> Copy email & password</>}
                </Button>
              </div>
              <Button
                variant='outline'
                className='w-full'
                onClick={() => { setAddClientOpen(false); setTempPwReveal(null); }}
                data-testid='add-client-done'
              >
                Done
              </Button>
            </div>
          ) : (
            <div className='space-y-3'>
              <div>
                <Label htmlFor='nc-name'>Full name *</Label>
                <Input
                  id='nc-name'
                  value={newClient.full_name}
                  onChange={(e)=>setNewClient({...newClient, full_name: e.target.value})}
                  placeholder='Jane Owner'
                  data-testid='nc-name-input'
                />
              </div>
              <div>
                <Label htmlFor='nc-email'>Email *</Label>
                <Input
                  id='nc-email'
                  type='email'
                  value={newClient.email}
                  onChange={(e)=>setNewClient({...newClient, email: e.target.value})}
                  placeholder='jane@example.com'
                  data-testid='nc-email-input'
                />
              </div>
              <div>
                <Label htmlFor='nc-phone'>Phone (optional)</Label>
                <Input
                  id='nc-phone'
                  value={newClient.phone}
                  onChange={(e)=>setNewClient({...newClient, phone: e.target.value})}
                  placeholder='555-0123'
                  data-testid='nc-phone-input'
                />
              </div>
              <p className='text-[11px] text-slate-500 dark:text-slate-400'>
                A temporary password will be generated and shown to you once. Share it with the client &mdash; they can change it after first login.
              </p>
              <DialogFooter>
                <Button variant='outline' onClick={() => setAddClientOpen(false)} data-testid='nc-cancel-btn'>Cancel</Button>
                <Button
                  onClick={submitNewClient}
                  disabled={creating}
                  className='bg-[#C8102E] hover:bg-[#A50D26] text-white font-semibold'
                  data-testid='nc-submit-btn'
                >
                  {creating ? 'Creating…' : 'Create client'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Dog dialog */}
      <Dialog open={addDogOpen} onOpenChange={setAddDogOpen}>
        <DialogContent className='max-w-md' data-testid='add-dog-dialog'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'><DogIcon className='w-5 h-5 text-[#1B3A87]' /> Add a dog</DialogTitle>
          </DialogHeader>
          <div className='space-y-3'>
            <div>
              <Label htmlFor='nd-name'>Dog&apos;s name *</Label>
              <Input id='nd-name' value={newDog.name} onChange={(e)=>setNewDog({...newDog, name: e.target.value})} data-testid='nd-name-input' />
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label htmlFor='nd-breed'>Breed</Label>
                <Input id='nd-breed' value={newDog.breed} onChange={(e)=>setNewDog({...newDog, breed: e.target.value})} data-testid='nd-breed-input' />
              </div>
              <div>
                <Label htmlFor='nd-sex'>Sex</Label>
                <select
                  id='nd-sex'
                  value={newDog.sex}
                  onChange={(e)=>setNewDog({...newDog, sex: e.target.value})}
                  className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
                  data-testid='nd-sex-input'
                >
                  <option value=''>&mdash;</option>
                  <option value='Male'>Male</option>
                  <option value='Female'>Female</option>
                </select>
              </div>
              <div>
                <Label htmlFor='nd-age'>Age (yrs)</Label>
                <Input id='nd-age' type='number' step='0.1' min='0' value={newDog.age_years} onChange={(e)=>setNewDog({...newDog, age_years: e.target.value})} data-testid='nd-age-input' />
              </div>
              <div>
                <Label htmlFor='nd-weight'>Weight (lbs)</Label>
                <Input id='nd-weight' type='number' min='0' value={newDog.weight_lbs} onChange={(e)=>setNewDog({...newDog, weight_lbs: e.target.value})} data-testid='nd-weight-input' />
              </div>
            </div>
            <p className='text-[11px] text-slate-500 dark:text-slate-400'>You can add a photo, medical info, and vet details after creating.</p>
            <DialogFooter>
              <Button variant='outline' onClick={()=>setAddDogOpen(false)} data-testid='nd-cancel-btn'>Cancel</Button>
              <Button
                onClick={submitNewDog}
                disabled={creatingDog}
                className='bg-[#1B3A87] hover:bg-[#16306F] text-white font-semibold'
                data-testid='nd-submit-btn'
              >
                {creatingDog ? 'Adding…' : 'Add dog'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
